import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ShoppingCart, Leaf, Check, Search, MapPin, ChevronDown, Plus, Minus, X, CalendarOff, AlertTriangle, ArrowUpDown, Zap } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useProducts } from '../context/ProductContext';
import { useCart } from '../context/CartContext';
import { ProductGridSkeleton } from '../components/common/Skeletons';
import ProductReviews from '../components/common/ProductReviews';
import { CountdownBadge } from '../components/common/FlashDealsSection';
import deliveryService from '../services/deliveryService';
import flashDealService from '../services/flashDealService';
import { fuzzyMatch, fuzzyScore } from '../lib/fuzzySearch';
import { applyFlashDiscount } from '../lib/variantUnits';
import api from '../services/api';

// Veg Icon Component (green square with dot)
const VegIcon = () => (
  <div className="w-4 h-4 border-2 border-green-600 rounded flex items-center justify-center flex-shrink-0">
    <div className="w-1.5 h-1.5 bg-green-600 rounded-full" />
  </div>
);

// Fallback icon for broken images
const ProductFallback = () => (
  <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
    <Leaf className="w-10 h-10 text-gray-300" />
  </div>
);

const toYMD = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

const FlashDealBanner = ({ count, onView, onDismiss }) => (
  <div className="mx-4 mt-4 rounded-xl flex items-center gap-3 p-3 pr-4 bg-orange-50 border border-orange-200">
    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 flex-shrink-0">
      <Zap className="w-4 h-4 text-orange-500" />
    </div>
    <p className="flex-1 text-sm font-medium text-orange-700">
      <span className="font-bold">Flash Deals Live!</span>
      {' '}Limited-time discounts available now.
    </p>
    <button
      onClick={onView}
      className="flex-shrink-0 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 px-3 py-1.5 rounded-lg transition-colors"
    >
      View Deals →
    </button>
    <button onClick={onDismiss} className="p-1 hover:bg-orange-100 rounded flex-shrink-0">
      <X className="w-4 h-4 text-orange-400" />
    </button>
  </div>
);

const Shop = () => {
  const { products, categories, loading: productsLoading } = useProducts();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [addedProducts, setAddedProducts] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('none'); // 'none' | 'low-to-high' | 'high-to-low'
  const [selectedLocation, setSelectedLocation] = useState('jaipur');
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [highlightedProduct, setHighlightedProduct] = useState(null);
  const [pendingScrollTo, setPendingScrollTo] = useState(null);
  const location = useLocation();
  const { addToCart, updateQuantity, removeFromCart, cartItems, openCart, cartItemsCount, isCartOpen } = useCart();
  // Tracks the chosen variant per product: { [productId]: variantId }
  const [selectedVariants, setSelectedVariants] = useState({});

  const getCartKey = (product, variantId) => {
    if (variantId) return `${product.id}_v${variantId}`;
    return product.id;
  };
  const getCartQty = (cartKey) => cartItems.find(i => i.id === cartKey)?.quantity || 0;
  const productsRef = useRef(null);

  const [holidayBanner, setHolidayBanner]       = useState(null);
  const [bannerDismissed, setBannerDismissed]   = useState(false);
  const [activeDeals, setActiveDeals]           = useState([]);
  const [flashBannerDismissed, setFlashBannerDismissed] = useState(
    () => sessionStorage.getItem('flash_banner_dismissed') === 'true'
  );

  const dealsByProductId = useMemo(() => {
    const map = {};
    activeDeals.forEach(d => { map[d.productId] = d; });
    return map;
  }, [activeDeals]);
  const flashDealCount = activeDeals.length;

  useEffect(() => {
    const dismissedKey = 'holiday_banner_dismissed';
    const dismissed = sessionStorage.getItem(dismissedKey);

    deliveryService.getUpcomingHolidays().then(result => {
      if (!result.success || !result.data.length) return;
      const todayYMD    = toYMD(new Date());
      const tomorrowYMD = toYMD(new Date(Date.now() + 86400000));
      const todayH      = result.data.find(h => h.date === todayYMD);
      const tomorrowH   = result.data.find(h => h.date === tomorrowYMD);

      if (todayH) {
        const key = `today_${todayH.date}`;
        if (dismissed === key) { setBannerDismissed(true); return; }
        setHolidayBanner({
          type: 'today',
          message: `We are not delivering today${todayH.isPublic && todayH.reason ? ` (${todayH.reason})` : ''}. You can still browse and place orders for tomorrow.`,
          date: todayH.date,
          key,
        });
      } else if (tomorrowH) {
        const key = `tomorrow_${tomorrowH.date}`;
        if (dismissed === key) { setBannerDismissed(true); return; }
        setHolidayBanner({
          type: 'tomorrow',
          message: `We won't be delivering tomorrow (${tomorrowH.date.split('-').reverse().join('/')})${tomorrowH.isPublic && tomorrowH.reason ? ` — ${tomorrowH.reason}` : ''}. Place your order today to receive it before then.`,
          date: tomorrowH.date,
          key,
        });
      }
    });
  }, []);

  const dismissBanner = () => {
    if (holidayBanner?.key) sessionStorage.setItem('holiday_banner_dismissed', holidayBanner.key);
    setBannerDismissed(true);
  };

  // Fetch active flash deals (public — visible to all users and guests)
  useEffect(() => {
    flashDealService.getActive()
      .then(deals => setActiveDeals(Array.isArray(deals) ? deals : []))
      .catch(() => {});
  }, []);

  // Log search queries ≥4 chars to backend after 500ms debounce
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 4) return;
    const timer = setTimeout(() => {
      api.post('/search-history', { query: trimmed, platform: 'web' }).catch(() => {});
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle navigation from CartDrawer: navigate('/shop', { state: { scrollToProduct: id } })
  useEffect(() => {
    const scrollTo = location.state?.scrollToProduct;
    if (!scrollTo) return;
    setSelectedCategory('all');
    setPendingScrollTo(scrollTo);
  }, [location.state]);

  // Handle navigation from the homepage Flash Deals: navigate('/shop', { state: { openProduct: id } })
  // Opens the product detail modal so the customer can pick a variant/size.
  useEffect(() => {
    const openId = location.state?.openProduct;
    if (!openId || !products?.length) return;
    const target = products.find(p => String(p.id) === String(openId));
    if (target) setSelectedProduct(target);
  }, [location.state, products]);

  // Once category is 'all' and products are rendered, scroll to and highlight the target product card
  useEffect(() => {
    if (!pendingScrollTo || selectedCategory !== 'all' || isLoading) return;
    const target = pendingScrollTo;
    setPendingScrollTo(null);
    // Delay lookup until after the product grid has painted
    setTimeout(() => {
      const el = document.getElementById(`regular-product-${target}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedProduct(target);
      setTimeout(() => setHighlightedProduct(null), 2000);
    }, 150);
  }, [selectedCategory, pendingScrollTo, isLoading]);

  const dismissFlashBanner = () => {
    sessionStorage.setItem('flash_banner_dismissed', 'true');
    setFlashBannerDismissed(true);
  };

  const goToFlashDeals = () => {
    handleCategorySelect('flash-deals');
  };

  // Handle category selection with scroll to top
  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    // Scroll to top of products section
    if (productsRef.current) {
      productsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 120, behavior: 'smooth' });
    }
  };

  const locations = [
    { id: 'jaipur', name: 'Jaipur' }
  ];

  const HolidayBanner = () => {
    if (!holidayBanner || bannerDismissed) return null;
    const isToday = holidayBanner.type === 'today';
    return (
      <div className={`mx-4 mt-4 rounded-xl flex items-start gap-3 p-4 border ${isToday ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
        {isToday
          ? <CalendarOff className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          : <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />}
        <p className={`flex-1 text-sm font-medium ${isToday ? 'text-red-700' : 'text-amber-700'}`}>{holidayBanner.message}</p>
        <button onClick={dismissBanner} className="p-1 hover:bg-black/10 rounded flex-shrink-0">
          <X className={`w-4 h-4 ${isToday ? 'text-red-400' : 'text-amber-400'}`} />
        </button>
      </div>
    );
  };

  // Use products loading state from context
  useEffect(() => {
    if (!productsLoading) {
      // Small delay for smooth transition
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setIsLoading(true);
    }
  }, [productsLoading, selectedCategory]);

  // Get product count for each category
  const getCategoryCount = (categoryId) => {
    const isSearching = searchQuery.trim().length > 0;
    const availableProducts = products.filter(p => isSearching ? p.isAvailable : p.inStock);
    if (categoryId === 'all') return availableProducts.length;
    if (categoryId === 'flash-deals') return availableProducts.filter(p => dealsByProductId[p.id]).length;
    return availableProducts.filter(p =>
      p.category === categoryId ||
      p.category === Number(categoryId) ||
      String(p.category) === String(categoryId)
    ).length;
  };

  // Group products by category
  const getGroupedProducts = () => {
    const isSearching = searchQuery.trim().length > 0;
    // While browsing: only in-stock products. While searching: include out-of-stock so customers can find them.
    let filteredProducts = products.filter(p => isSearching ? p.isAvailable : p.inStock);
    
    // Filter by search query (fuzzy — handles typos, misspellings, and Hindi)
    // Also sort by match quality so Hindi alias hits rank above loose fuzzy matches
    if (searchQuery.trim()) {
      filteredProducts = filteredProducts
        .map(p => {
          // Score against product name (primary)
          let score = fuzzyScore(searchQuery, p.name);
          // Also score against each keyword — keywords get a slight penalty (score + 2) so name matches rank higher
          if (p.keywords) {
            for (const kw of p.keywords.split(',')) {
              const trimmed = kw.trim();
              if (!trimmed) continue;
              const kwScore = fuzzyScore(searchQuery, trimmed);
              if (kwScore !== -1) {
                const penalized = kwScore + 2;
                score = score === -1 ? penalized : Math.min(score, penalized);
              }
            }
          }
          return { product: p, score };
        })
        .filter(entry => entry.score !== -1)
        .sort((a, b) => a.score - b.score)
        .map(entry => entry.product);
    }
    
    // Sort by effective price (flash deal price if active, otherwise regular price)
    if (sortOrder === 'low-to-high' || sortOrder === 'high-to-low') {
      const getEffectivePrice = (p) =>
        dealsByProductId[p.id] ? Number(dealsByProductId[p.id].discountedPrice) : Number(p.price);
      filteredProducts = [...filteredProducts].sort((a, b) =>
        sortOrder === 'low-to-high'
          ? getEffectivePrice(a) - getEffectivePrice(b)
          : getEffectivePrice(b) - getEffectivePrice(a)
      );
    }

    // While searching, show one flat result set (in-stock first, then out-of-stock)
    // instead of splitting matches across category sections.
    if (isSearching) {
      const inStock = filteredProducts.filter(p => p.inStock);
      const oos = filteredProducts.filter(p => !p.inStock);
      const groups = [];
      if (inStock.length) groups.push({ category: { id: 'search-results', name: 'Search results' }, products: inStock });
      if (oos.length) groups.push({ category: { id: 'search-oos', name: 'Out of Stock' }, products: oos });
      return groups;
    }

    if (selectedCategory === 'flash-deals') {
      return [{
        category: { id: 'flash-deals', name: 'Flash Deals' },
        products: filteredProducts.filter(p => dealsByProductId[p.id]),
      }];
    }

    if (selectedCategory !== 'all') {
      const selectedCat = categories.find(c =>
        c.id === selectedCategory ||
        c.id === Number(selectedCategory) ||
        String(c.id) === String(selectedCategory)
      );
      return [{
        category: selectedCat,
        products: filteredProducts.filter(p =>
          p.category === selectedCategory ||
          p.category === Number(selectedCategory) ||
          String(p.category) === String(selectedCategory)
        )
      }];
    }
    
    return categories
      .filter(cat => cat.id !== 'all' && cat.name?.toLowerCase() !== 'uncategorized')
      .map(category => ({
        category,
        products: filteredProducts.filter(p => 
          p.category === category.id ||
          p.category === Number(category.id) ||
          String(p.category) === String(category.id)
        )
      }))
      .filter(group => group.products.length > 0);
  };

  const groupedProducts = getGroupedProducts();

  const handleAddToCart = (product, fromFlashSection = false) => {
    const deal = dealsByProductId[product.id];
    // For variant products, default to the first variant (matches the UI's
    // default-first pill) when the user hasn't explicitly picked one.
    const variantId = (product.variants?.length > 0)
      ? (selectedVariants[product.id] ?? product.variants[0].id)
      : null;
    const variant = variantId ? (product.variants || []).find(v => v.id === variantId) : null;
    const effectivePrice = variant ? variant.price : Number(product.price);

    const productToAdd = (deal && fromFlashSection)
      ? {
          ...product,
          id: deal.flashSku,
          productId: product.id,
          // Discount the variant-or-base price by the deal's percent, not the
          // deal's base-only discountedPrice.
          price: applyFlashDiscount(effectivePrice, deal.discountPercent),
          originalPrice: effectivePrice,
          flashSku: deal.flashSku ?? null,
        }
      : { ...product, price: effectivePrice };
    addToCart(productToAdd, variantId);
    setAddedProducts(prev => ({ ...prev, [product.id]: true }));
    setTimeout(() => {
      setAddedProducts(prev => ({ ...prev, [product.id]: false }));
    }, 1500);
  };

  const handleImageError = (id) => {
    setImageErrors(prev => ({ ...prev, [id]: true }));
  };

  return (
    <div className="shop-page bg-white min-h-screen pt-20">
      {flashDealCount > 0 && !flashBannerDismissed && (
        <FlashDealBanner count={flashDealCount} onView={goToFlashDeals} onDismiss={dismissFlashBanner} />
      )}
      <HolidayBanner />
      {/* Search / Filter Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-20 z-30 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            {/* Search Bar */}
            <div className="relative w-full sm:w-64 md:w-80 flex-shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search for vegetables, fruits..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#548B2E] focus:border-[#548B2E] focus:bg-white transition-all text-sm"
              />
            </div>

            {/* Sort + Location row (side-by-side on all screen sizes) */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Sort Dropdown */}
              <div className="relative flex-1 sm:flex-none">
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="appearance-none w-full pl-8 pr-7 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-700 font-medium hover:bg-white focus:outline-none focus:ring-2 focus:ring-[#548B2E] focus:border-[#548B2E] transition-colors cursor-pointer text-sm whitespace-nowrap"
                >
                  <option value="none">Sort by Price</option>
                  <option value="low-to-high">Low to High</option>
                  <option value="high-to-low">High to Low</option>
                </select>
                <ArrowUpDown className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              </div>

              {/* Location Dropdown */}
              <div className="relative flex-1 sm:flex-none">
                <button
                  onClick={() => setIsLocationOpen(!isLocationOpen)}
                  className="flex items-center gap-1.5 w-full px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-700 font-medium hover:bg-white transition-colors text-sm whitespace-nowrap"
                >
                  <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="truncate">
                    <span className="hidden xs:inline">Deliver: </span>
                    <strong className="text-gray-800">{locations.find(l => l.id === selectedLocation)?.name}</strong>
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-auto" />
                </button>

                {isLocationOpen && (
                  <div className="absolute top-full mt-1 right-0 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-[160px] overflow-hidden">
                    {locations.map((location) => (
                      <button
                        key={location.id}
                        onClick={() => {
                          setSelectedLocation(location.id);
                          setIsLocationOpen(false);
                        }}
                        className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700 text-sm"
                      >
                        <MapPin className="w-4 h-4 text-gray-500" />
                        {location.name}
                        {selectedLocation === location.id && (
                          <Check className="w-4 h-4 text-green-600 ml-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Categories — hidden while searching */}
          {!searchQuery.trim() && <div className="lg:w-56 flex-shrink-0 lg:border-r lg:border-gray-200 lg:pr-6">
            <div className="lg:sticky lg:top-[148px]">
              <h3 className="hidden lg:block text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Categories</h3>

              {/* Mobile: flex-row scrollable | Desktop: flex-col sidebar */}
              <div className="flex flex-row gap-2 overflow-x-auto pb-1 lg:flex-col lg:gap-0 lg:space-y-1 lg:overflow-visible lg:pb-0"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>

                {activeDeals.length > 0 && (
                  <button
                    onClick={() => handleCategorySelect('flash-deals')}
                    className={`
                      shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors
                      lg:w-full lg:rounded-none lg:px-2 lg:py-2.5 lg:text-left lg:flex lg:items-center lg:justify-between
                      ${selectedCategory === 'flash-deals'
                        ? 'bg-orange-500 text-white lg:bg-transparent lg:text-orange-600 lg:font-semibold'
                        : 'bg-gray-100 text-gray-600 lg:bg-transparent hover:text-gray-900'}
                    `}
                  >
                    <span className="flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-orange-500 lg:text-orange-500 shrink-0" />
                      Flash Deals
                    </span>
                    <span className={`hidden lg:inline text-xs px-2 py-0.5 rounded-full ${
                      selectedCategory === 'flash-deals' ? 'bg-orange-100 text-orange-600' : 'bg-orange-50 text-orange-500'
                    }`}>
                      {activeDeals.length}
                    </span>
                  </button>
                )}

                {categories.filter(cat => cat.name?.toLowerCase() !== 'uncategorized').map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategorySelect(category.id)}
                    className={`
                      shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors
                      lg:w-full lg:rounded-none lg:px-2 lg:py-2.5 lg:text-left lg:flex lg:items-center lg:justify-between
                      ${selectedCategory === category.id
                        ? 'bg-[#548B2E] text-white lg:bg-transparent lg:text-green-600 lg:font-semibold'
                        : 'bg-gray-100 text-gray-600 lg:bg-transparent hover:text-gray-900'}
                    `}
                  >
                    <span className="lg:text-sm">{category.name}</span>
                    <span className={`hidden lg:inline text-xs px-2 py-0.5 rounded-full ${
                      selectedCategory === category.id ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {getCategoryCount(category.id)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>}

          {/* Right Side - Products */}
          <div className="flex-1" ref={productsRef}>
            {isLoading ? (
              <ProductGridSkeleton count={6} />
            ) : (
              <div className="space-y-10">
                {groupedProducts.map((group) => {
                  const isFlashSection = group.category.id === 'flash-deals';
                  return (
                  <div key={group.category.id}>
                    {/* Category Header */}
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-bold text-gray-800">{group.category.name}</h2>
                      <span className="text-sm text-gray-500">{group.products.length} items</span>
                    </div>

                    {/* Product Cards Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {group.products.map((product) => {
                        const deal = isFlashSection ? dealsByProductId[product.id] : null;
                        return (
                        <div
                          key={product.id}
                          id={!isFlashSection ? `regular-product-${product.id}` : undefined}
                          onClick={() => setSelectedProduct(product)}
                          className={`bg-white rounded-lg border transition-all duration-300 overflow-hidden group cursor-pointer ${!product.inStock ? 'opacity-75' : ''} ${!isFlashSection && highlightedProduct === product.id ? 'border-green-500 ring-2 ring-green-400 ring-offset-1 shadow-lg' : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'}`}
                        >
                          {/* Product Image */}
                          <div className="relative aspect-square bg-gray-50 p-3 overflow-hidden">
                            {imageErrors[product.id] ? (
                              <ProductFallback />
                            ) : (
                              <img
                                src={product.image}
                                alt={product.name}
                                className={`w-full h-full object-cover rounded-lg transition-transform duration-300 ${product.inStock ? 'group-hover:scale-105' : 'grayscale'}`}
                                onError={() => handleImageError(product.id)}
                                loading="lazy"
                              />
                            )}
                            <div className="absolute top-2 left-2 z-10 bg-white rounded p-0.5">
                              <VegIcon />
                            </div>
                            {/* Out of Stock Badge */}
                            {!product.inStock && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                                <span className="bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                                  Out of Stock
                                </span>
                              </div>
                            )}
                            {/* Flash Deal Badge — only in flash deals section */}
                            {deal && (
                              <div className="absolute top-2 right-2 z-10 bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                                <Zap className="w-2.5 h-2.5" />
                                {deal.discountPercent}% OFF
                              </div>
                            )}
                            {/* Flash Deal Countdown overlay */}
                            {deal && (
                              <div className="absolute bottom-0 left-0 right-0 bg-black/60 py-1 text-center flex items-center justify-center z-10 rounded-b-lg">
                                <CountdownBadge endsAt={deal.endsAt} className="text-white font-bold" />
                              </div>
                            )}
                            {/* Low Stock Badge */}
                            {product.inStock && product.stockQuantity > 0 && product.stockQuantity <= 5 && (
                              <div className={`absolute right-2 z-10 ${deal ? 'top-9' : 'top-2'}`}>
                                <span className="bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                  Only {product.stockQuantity} left
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Product Info */}
                          <div className="p-2 sm:p-4">
                            <h3 className="font-medium text-gray-800 text-sm leading-tight mb-1 line-clamp-2">
                              {product.name}
                            </h3>

                            {/* Variant pills — shown only when variants exist */}
                            {product.variants?.length > 0 ? (
                              <div className="flex flex-wrap gap-1 mb-2">
                                {product.variants.map(v => {
                                  const isSelected = (selectedVariants[product.id] ?? product.variants[0].id) === v.id;
                                  const outOfStock = !v.isAvailable || v.stockQuantity === 0;
                                  return (
                                    <button
                                      key={v.id}
                                      type="button"
                                      disabled={outOfStock}
                                      onClick={e => { e.stopPropagation(); setSelectedVariants(prev => ({ ...prev, [product.id]: v.id })); }}
                                      className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                                        outOfStock ? 'opacity-40 cursor-not-allowed border-gray-200 text-gray-400'
                                          : isSelected ? 'bg-[#548B2E] text-white border-[#548B2E]'
                                          : 'border-gray-300 text-gray-600 hover:border-[#548B2E]'
                                      }`}
                                    >
                                      {v.label}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-500 mb-2">{product.weight}</p>
                            )}

                            <div className="flex items-center justify-between">
                              {(() => {
                                const activeVariantId = product.variants?.length > 0
                                  ? (selectedVariants[product.id] ?? product.variants[0]?.id)
                                  : null;
                                const activeVariant = activeVariantId ? product.variants.find(v => v.id === activeVariantId) : null;
                                const displayPrice = activeVariant ? activeVariant.price : Number(product.price);
                                // Discount the SELECTED variant's price by the deal's percent,
                                // not the deal's base-only discountedPrice.
                                const dealPrice = deal ? applyFlashDiscount(displayPrice, deal.discountPercent) : null;
                                return deal ? (
                                  <div className="flex flex-col">
                                    <span className="text-sm sm:text-lg font-bold text-orange-600">₹{dealPrice}</span>
                                    <span className="text-xs text-gray-400 line-through leading-none">₹{displayPrice}</span>
                                  </div>
                                ) : (
                                  <span className="text-sm sm:text-lg font-bold text-gray-900">₹{displayPrice}</span>
                                );
                              })()}

                              {(() => {
                                const activeVariantId = product.variants?.length > 0
                                  ? (selectedVariants[product.id] ?? product.variants[0]?.id)
                                  : null;
                                const cartKey = isFlashSection && deal ? deal.flashSku : getCartKey(product, activeVariantId);
                                const qty = getCartQty(cartKey);
                                const activeVariant = activeVariantId ? product.variants?.find(v => v.id === activeVariantId) : null;
                                const isInStock = activeVariant
                                  ? activeVariant.isAvailable && activeVariant.stockQuantity > 0
                                  : product.inStock;
                                return !isInStock ? (
                                  <span className="px-2 py-1 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed">
                                    Sold Out
                                  </span>
                                ) : qty > 0 ? (
                                  <div
                                    className="flex items-center bg-green-600 rounded-lg overflow-hidden"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      onClick={() => {
                                        if (qty === 1) removeFromCart(cartKey);
                                        else updateQuantity(cartKey, qty - 1);
                                      }}
                                      className="px-1.5 py-1.5 sm:px-2 sm:py-2 text-white hover:bg-green-700 transition-colors"
                                    >
                                      <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                                    </button>
                                    <span className="px-1 text-white text-xs sm:text-sm font-semibold min-w-[1rem] text-center">
                                      {qty}
                                    </span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (isFlashSection && deal) {
                                          toast.warning('Only 1 Flash Deal unit can be added per item.', {
                                            description: 'Navigating to the regular listing.',
                                          });
                                          setSelectedCategory('all');
                                          setPendingScrollTo(product.id);
                                          return;
                                        }
                                        handleAddToCart(product, isFlashSection);
                                      }}
                                      className="px-1.5 py-1.5 sm:px-2 sm:py-2 text-white hover:bg-green-700 transition-colors"
                                    >
                                      <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleAddToCart(product, isFlashSection); }}
                                    className="flex items-center gap-0.5 sm:gap-1 px-2 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium bg-green-600 text-white hover:bg-green-700 active:scale-95 transition-all duration-300"
                                  >
                                    <Plus className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                    <span>Add</span>
                                  </button>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                        ); })}
                    </div>
                  </div>
                  ); })}

                {groupedProducts.length === 0 && (
                  <div className="text-center py-20">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search className="w-10 h-10 text-gray-300" />
                    </div>
                    <p className="text-gray-600 text-lg">No products found</p>
                    <p className="text-gray-400 text-sm mt-1">Try adjusting your search or filter</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Category Pills - Fixed at bottom — hidden while searching */}
      {!searchQuery.trim() && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
          <div className="flex gap-2 overflow-x-auto p-3 scrollbar-hide">
            {activeDeals.length > 0 && (
              <button
                onClick={() => handleCategorySelect('flash-deals')}
                className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all flex items-center gap-1.5 ${
                  selectedCategory === 'flash-deals'
                    ? 'bg-orange-500 text-white'
                    : 'bg-orange-50 text-orange-600 border border-orange-200'
                }`}
              >
                <Zap className="w-3.5 h-3.5" />
                Flash Deals
              </button>
            )}
            {categories.filter(cat => cat.name?.toLowerCase() !== 'uncategorized').map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategorySelect(category.id)}
                className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 border border-gray-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bottom padding for mobile — only needed when category bar is visible */}
      {!searchQuery.trim() && <div className="h-20 lg:hidden" />}
      
      {/* Floating Cart Button */}
      <button
        onClick={openCart}
        className={`fixed bottom-6 right-6 lg:bottom-8 lg:right-8 z-50 flex items-center gap-3 bg-gray-900 text-white px-6 py-4 rounded-full shadow-xl hover:bg-gray-800 transition-all hover:scale-105 active:scale-95 ${isCartOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
      >
        <ShoppingCart className="w-5 h-5" />
        <span className="font-medium">View Cart</span>
        {cartItemsCount > 0 && (
          <span className="bg-white text-gray-900 text-xs font-bold min-w-[24px] h-6 rounded-full flex items-center justify-center px-2">
            {cartItemsCount}
          </span>
        )}
      </button>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
          onClick={() => setSelectedProduct(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold text-gray-800">Product Details</h2>
              <button
                onClick={() => setSelectedProduct(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Product Info */}
            <div className="p-6">
              <div className="flex flex-col md:flex-row gap-6 mb-8">
                {/* Product Image */}
                <div className="w-full md:w-1/2">
                  <div className="aspect-square bg-gray-50 rounded-xl overflow-hidden">
                    {imageErrors[selectedProduct.id] ? (
                      <ProductFallback />
                    ) : (
                      <img
                        src={selectedProduct.image}
                        alt={selectedProduct.name}
                        className="w-full h-full object-cover"
                        onError={() => handleImageError(selectedProduct.id)}
                      />
                    )}
                  </div>
                </div>

                {/* Product Details */}
                <div className="w-full md:w-1/2 flex flex-col justify-between">
                  {(() => {
                    // Variant-aware, flash-aware pricing for the detail modal.
                    const mDeal = dealsByProductId[selectedProduct.id] ?? null;
                    const mVariants = selectedProduct.variants ?? [];
                    const mActiveVariantId = mVariants.length > 0
                      ? (selectedVariants[selectedProduct.id] ?? mVariants[0]?.id)
                      : null;
                    const mActiveVariant = mActiveVariantId
                      ? mVariants.find(v => v.id === mActiveVariantId)
                      : null;
                    const mUnitPrice = mActiveVariant ? Number(mActiveVariant.price) : Number(selectedProduct.price);
                    const mDisplayPrice = mDeal ? applyFlashDiscount(mUnitPrice, mDeal.discountPercent) : mUnitPrice;
                    const mVariantInStock = mActiveVariant
                      ? (mActiveVariant.isAvailable && mActiveVariant.stockQuantity > 0)
                      : selectedProduct.inStock;
                    return (
                  <>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">{selectedProduct.name}</h1>
                    <p className="text-gray-500 mb-4">{mActiveVariant ? mActiveVariant.label : selectedProduct.weight}</p>

                    {/* Variant selector — mirrors the product card pills */}
                    {mVariants.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Select size</p>
                        <div className="flex flex-wrap gap-2">
                          {mVariants.map(v => {
                            const isSelected = mActiveVariantId === v.id;
                            const outOfStock = !v.isAvailable || v.stockQuantity === 0;
                            const vPrice = mDeal ? applyFlashDiscount(Number(v.price), mDeal.discountPercent) : Number(v.price);
                            return (
                              <button
                                key={v.id}
                                type="button"
                                disabled={outOfStock}
                                onClick={() => setSelectedVariants(prev => ({ ...prev, [selectedProduct.id]: v.id }))}
                                className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                                  outOfStock ? 'opacity-40 cursor-not-allowed border-gray-200 text-gray-400'
                                    : isSelected ? 'bg-[#548B2E] text-white border-[#548B2E]'
                                    : 'border-gray-300 text-gray-700 hover:border-[#548B2E]'
                                }`}
                              >
                                <span className="font-medium">{v.label}</span>
                                <span className="block text-xs opacity-90">
                                  {outOfStock ? 'Out of stock' : `₹${mDeal ? vPrice : Number(vPrice).toFixed(2)}`}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="flex items-baseline gap-3 mb-4">
                      <span className="text-3xl font-bold text-gray-900">₹{mDeal ? mDisplayPrice : Number(mDisplayPrice).toFixed(2)}</span>
                      {mDeal && (
                        <div className="flex items-center gap-2">
                          <span className="text-lg text-gray-400 line-through">₹{Number(mUnitPrice).toFixed(2)}</span>
                          <span className="text-sm text-orange-600 font-medium bg-orange-50 px-2 py-0.5 rounded">
                            {mDeal.discountPercent}% off
                          </span>
                        </div>
                      )}
                    </div>

                    {mDeal && (
                      <div className="mb-4 flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500">Deals ends in:</span>
                        <CountdownBadge endsAt={mDeal.endsAt} className="text-orange-600 font-bold" />
                      </div>
                    )}

                    {!mVariantInStock ? (
                      <p className="text-red-500 font-medium mb-4">Out of Stock</p>
                    ) : !mActiveVariant && selectedProduct.stockQuantity > 0 && selectedProduct.stockQuantity <= 5 ? (
                      <p className="mb-4">
                        <span className="text-orange-600 font-medium">Only {selectedProduct.stockQuantity} left in stock!</span>
                      </p>
                    ) : null}

                    {selectedProduct.description && (
                      <div className="mb-6">
                        <p className="text-gray-600 text-sm leading-relaxed">{selectedProduct.description}</p>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleAddToCart(selectedProduct, mDeal != null)}
                    disabled={addedProducts[selectedProduct.id] || !mVariantInStock}
                    className={`w-full py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                      !mVariantInStock
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : addedProducts[selectedProduct.id]
                        ? 'bg-green-600 text-white'
                        : 'bg-[#548B2E] text-white hover:bg-[#3f6b20] active:scale-95'
                    }`}
                  >
                    {!mVariantInStock ? (
                      'Out of Stock'
                    ) : addedProducts[selectedProduct.id] ? (
                      <><Check className="w-5 h-5" /> Added to Cart</>
                    ) : (
                      <><Plus className="w-5 h-5" /> Add to Cart</>
                    )}
                  </button>
                  </>
                    );
                  })()}
                </div>
              </div>

              {/* Reviews Section */}
              <div className="border-t border-gray-100 pt-6">
                <ProductReviews productId={selectedProduct.id} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;